import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Button, Checkbox, Collapse, Descriptions, Empty, Input, InputNumber, Modal, notification, Popover, Select, Spin, Tag, Typography } from 'antd';
import { CaretDownOutlined, CaretUpOutlined, CheckOutlined, CloseOutlined, DeleteOutlined, InstagramOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import axiosApiInstance from '../../../../api/axiosClient';
import { userAtom } from '../../../../context/auth_atoms';
import {
    useCampaignQuery, useCampaignRoster, useCampaignAvatars, useAppendCreators, useUpdateCampaign, useUpdateCreatorMeta,
    useAddCreatorDeliverable, useUpdateCreatorDeliverable, useDeleteCreatorDeliverable,
    useCreatorColumnPref, useSaveCreatorColumns, useFetchCreatorStory,
} from '../queryHooks';
import type { Campaign, CampaignCreator, CampaignCreatorDeliverable } from '../types';
import { generate } from '@ant-design/colors';
import { YT_ICON, IG_ICON, TT_ICON, formatFollowers, getPaletteColor } from '../../Creators/utils';
import CreatorDetailContent from '../../Creators/CreatorDetailContent';
import CreatorPanel, { CreatorPanelMeta } from '../components/CreatorPanel';
import DeliverableManager, { DeliverableDraft } from '../components/DeliverableManager';
import { audienceTier, creatorCountry, STAGE_OPTIONS, totalAudience } from '../format';

/** Opens creator detail as a centered modal (same style as the Creators page). */
const CreatorDrawer: React.FC<{
    creatorId: number | null;
    onClose: () => void;
}> = ({ creatorId, onClose }) => (
    <Modal
        open={creatorId !== null}
        onCancel={onClose}
        width={1000}
        title="Creator Details"
        footer={null}
        destroyOnClose
    >
        {creatorId !== null && (
            <React.Suspense fallback={<div style={{ textAlign: 'center', paddingTop: 60 }}><Spin /></div>}>
                <CreatorDetailContent id={String(creatorId)} />
            </React.Suspense>
        )}
    </Modal>
);

const CAMPAIGN_CREATOR_COLS = [
    { key: 'country',      label: 'Country' },
    { key: 'stage',        label: 'Relationship stage' },
    { key: 'tier',         label: 'Tier' },
    { key: 'tags',         label: 'Tags' },
    { key: 'ig_audience',  label: 'Instagram audience' },
    { key: 'approved',     label: 'Approved' },
    { key: 'pillar',       label: 'Pillar' },
    { key: 'yt',           label: 'YT Followers' },
    { key: 'ig',           label: 'IG Followers' },
    { key: 'tt',           label: 'TT Followers' },
    { key: 'budget',       label: 'Budget (€)' },
    { key: 'comments',     label: 'Comments' },
    { key: 'deliverables', label: 'Deliverables' },
    { key: 'audience',     label: 'Total audience' },
    { key: 'city',         label: 'City' },
    { key: 'email',        label: 'Email' },
    { key: 'phone',        label: 'Phone' },
    { key: 'main_platform', label: 'Main platform' },
    { key: 'handles',      label: 'Handles' },
    { key: 'engagement',   label: 'Engagement rate' },
    { key: 'commission',   label: 'Commission' },
    { key: 'paid',         label: 'Paid' },
    { key: 'payment_status', label: 'Payment status' },
    { key: 'contract_status', label: 'Contract status' },
] as const;
type ColKey = typeof CAMPAIGN_CREATOR_COLS[number]['key'];
const COLS_STORAGE_KEY = 'campaign-view-creator-cols-v2';
const DEFAULT_VISIBLE: ColKey[] = ['country', 'stage', 'tier', 'tags', 'ig_audience', 'approved', 'yt', 'ig', 'tt', 'budget', 'deliverables'];

function accountColKey(uid?: string): string {
    return uid ? `${COLS_STORAGE_KEY}:${uid}` : COLS_STORAGE_KEY;
}

function knownCols(keys: string[]): ColKey[] {
    const known = new Set<string>(CAMPAIGN_CREATOR_COLS.map((c) => c.key));
    return keys.filter((k): k is ColKey => known.has(k));
}

function storedCols(key: string): ColKey[] | null {
    try {
        const stored = localStorage.getItem(key);
        if (!stored) return null;
        const parsed = knownCols(JSON.parse(stored) as string[]);
        return parsed.length ? parsed : null;
    } catch {
        return null;
    }
}
const EXTRA_COLS = new Set<ColKey>(['audience', 'city', 'email', 'phone', 'main_platform', 'handles', 'engagement', 'commission', 'paid', 'payment_status', 'contract_status']);
type CreatorMetaDraft = {
    budget: number | null;
    comments: string;
    approved: boolean | null;
    pillar: string;
    relationship_stage: string;
    commission: number | null;
    paid_amount: number | null;
    payment_status: string;
    contract_url: string;
    contract_status: string;
    po_number: string;
};
const EMPTY_META: CreatorMetaDraft = {
    budget: null, comments: '', approved: null, pillar: '', relationship_stage: 'unaware',
    commission: null, paid_amount: null, payment_status: 'unpaid',
    contract_url: '', contract_status: '', po_number: '',
};

function accountHandle(key?: string): string {
    if (!key) return '';
    const raw = key.trim();
    try {
        const u = new URL(raw.includes('://') ? raw : `https://${raw}`);
        const parts = u.pathname.split('/').filter(Boolean);
        return (parts[parts.length - 1] || '').replace(/^@/, '');
    } catch {
        return raw.replace(/^@/, '');
    }
}

function bestEngagement(c: CampaignCreator): number | null {
    const rates = (c.accounts ?? [])
        .map((a) => a.engagement_rate)
        .filter((r): r is number => r != null && r > 0);
    if (rates.length === 0) return null;
    return Math.max(...rates) * 100;
}

function money(n: number | null | undefined): string {
    if (n == null) return '—';
    return `€${n.toLocaleString()}`;
}

function Muted() {
    return <span style={{ color: '#d9d9d9' }}>—</span>;
}

function CreatorTags({ creator }: { creator: CampaignCreator }) {
    const tags = creator.tags ?? [];
    if (tags.length === 0) return <Muted />;
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 280 }}>
            {tags.map((tag) => {
                const label = tag.TagCategoryValue?.value;
                if (!label) return null;
                const bg = tag.TagCategory?.color || '#d9d9d9';
                const palette = bg.startsWith('#') ? generate(bg) : [];
                return (
                    <Tag
                        key={tag.id ?? label}
                        color={bg}
                        title={tag.TagCategory?.category_name}
                        style={{ margin: 0, color: palette.length ? getPaletteColor(bg, palette) : undefined }}
                    >
                        {label}
                    </Tag>
                );
            })}
        </div>
    );
}

const PLATFORM_ICON: Record<string, React.ReactNode> = { yt: YT_ICON, ig: IG_ICON, tt: TT_ICON };

const statusColor = (s?: string) => {
    switch (s) {
        case 'spec':
            return 'gold';
        case 'active':
            return 'green';
        case 'completed':
            return 'blue';
        case 'canceled':
        case 'cancelled':
            return 'red';
        default:
            return 'default';
    }
};

const thStyle: React.CSSProperties = {
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: 14,
    fontWeight: 600,
    color: '#595959',
    whiteSpace: 'nowrap',
    background: '#fff',
};

const tdStyle: React.CSSProperties = {
    padding: '12px',
    verticalAlign: 'middle',
    fontSize: 15,
    background: '#fff',
};
const CreatorDeliverables: React.FC<{
    campaignId: number;
    creatorId: number;
    deliverables: CampaignCreatorDeliverable[];
    embedded?: boolean;
    onSaved?: () => void;
}> = ({ campaignId, creatorId, deliverables, embedded, onSaved }) => {
    const addDeliverable = useAddCreatorDeliverable();
    const updateDeliverable = useUpdateCreatorDeliverable();
    const deleteDeliverable = useDeleteCreatorDeliverable();

    const [modalOpen, setModalOpen] = useState(false);
    const saving = addDeliverable.isLoading || updateDeliverable.isLoading || deleteDeliverable.isLoading;
    // Shown count. A ref so a second click in the same moment stacks on the first.
    const progressRef = useRef<Record<number, number>>({});
    const [progress, setProgress] = useState<Record<number, number>>({});
    const saveTarget = useRef<Record<number, { record: CampaignCreatorDeliverable | DeliverableDraft; completed: number }>>({});
    const saveBusy = useRef<Record<number, boolean>>({});

    useEffect(() => {
        setProgress((prev) => {
            let changed = false;
            const next = { ...prev };
            for (const d of deliverables) {
                if (next[d.id] === d.completed) {
                    delete next[d.id];
                    delete progressRef.current[d.id];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [deliverables]);

    const shown = deliverables.map((d) => (
        progress[d.id] != null ? { ...d, completed: progress[d.id] } : d
    ));

    const bodyOf = (d: DeliverableDraft) => ({
        platform: d.platform,
        post_type: d.post_type,
        quantity: d.quantity,
        completed: d.completed ?? 0,
        cost_per_post: d.cost_per_post ?? null,
        review_by: d.review_by ?? null,
        publish_by: d.publish_by ?? null,
        usage_rights: !!d.usage_rights,
        boosted_cost: d.boosted_cost ?? null,
        boosted_on: d.boosted_on ?? null,
        rate: d.rate ?? null,
    });

    const handleCreate = async (d: DeliverableDraft) => {
        try {
            await addDeliverable.mutateAsync({ campaignId, creatorId, ...bodyOf(d) });
            onSaved?.();
        } catch (err) {
            notification.error({ message: 'Failed to add deliverable' });
            throw err;
        }
    };
    const handleUpdate = async (_index: number, d: DeliverableDraft) => {
        if (!d.id) return;
        try {
            await updateDeliverable.mutateAsync({ campaignId, creatorId, deliverableId: d.id, ...bodyOf(d) });
            onSaved?.();
        } catch (err) {
            notification.error({ message: 'Failed to save deliverable' });
            throw err;
        }
    };
    const handleDelete = async (_index: number, d: DeliverableDraft) => {
        if (!d.id) return;
        try {
            await deleteDeliverable.mutateAsync({ campaignId, creatorId, deliverableId: d.id });
            onSaved?.();
        } catch (err) {
            notification.error({ message: 'Failed to delete deliverable' });
            throw err;
        }
    };

    const applyProgress = (d: CampaignCreatorDeliverable | DeliverableDraft, completed: number) => {
        if (!d.id) return;
        const clamped = Math.max(0, Math.min(d.quantity, completed));
        progressRef.current = { ...progressRef.current, [d.id]: clamped };
        setProgress(progressRef.current);
        saveTarget.current[d.id] = { record: d, completed: clamped };
        void drainProgress(d.id);
    };

    const drainProgress = async (id: number) => {
        if (saveBusy.current[id]) return;
        saveBusy.current[id] = true;
        try {
            while (saveTarget.current[id]) {
                const job = saveTarget.current[id];
                delete saveTarget.current[id];
                await updateDeliverable.mutateAsync({
                    campaignId: job.record.campaign_id || campaignId,
                    creatorId: job.record.creator_id || creatorId,
                    deliverableId: id,
                    ...bodyOf({ ...job.record, completed: job.completed }),
                });
            }
        } catch {
            delete saveTarget.current[id];
            const next = { ...progressRef.current };
            delete next[id];
            progressRef.current = next;
            setProgress(next);
            notification.error({ message: 'Failed to update deliverable' });
        } finally {
            saveBusy.current[id] = false;
            if (saveTarget.current[id]) void drainProgress(id);
        }
    };

    const stepProgress = (d: CampaignCreatorDeliverable | DeliverableDraft, delta: number) => {
        if (!d.id) return;
        const cur = progressRef.current[d.id] ?? d.completed ?? 0;
        const next = Math.max(0, Math.min(d.quantity, cur + delta));
        if (next === cur) return;
        applyProgress(d, next);
    };

    const handleToggleCompleted = (d: CampaignCreatorDeliverable) => {
        const cur = progressRef.current[d.id] ?? d.completed ?? 0;
        applyProgress(d, cur >= d.quantity ? 0 : cur + 1);
    };

    const editor = (
        <DeliverableManager
            items={shown}
            saving={saving}
            showProgress
            onStep={(_index, d, delta) => stepProgress(d, delta)}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
        />
    );

    if (embedded) return editor;

    return (
        <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                {shown.map((d) => {
                    const done = d.completed >= d.quantity;
                    return (
                        <span
                            key={d.id}
                            onClick={() => handleToggleCompleted(d)}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                cursor: 'pointer', fontSize: 14,
                                color: done ? '#52c41a' : '#262626',
                                fontWeight: done ? 600 : 400,
                            }}
                        >
                            {PLATFORM_ICON[d.platform]}
                            <span>{d.completed}/{d.quantity}</span>
                        </span>
                    );
                })}
                <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} />
            </div>
            <Modal
                open={modalOpen}
                title="Deliverables"
                onCancel={() => setModalOpen(false)}
                footer={null}
                destroyOnClose
                width={640}
            >
                {editor}
            </Modal>
        </>
    );
};

/**
 * CreatorsSection — campaign roster with ops columns + tracker fields.
 */
const CreatorsSection: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const campaignId = id ? Number(id) : undefined;

    const shell = useCampaignQuery(campaignId);
    const roster = useCampaignRoster(campaignId);
    const avatars = useCampaignAvatars(campaignId);
    const avatarByCreator = useMemo(() => {
        const m: Record<number, string> = {};
        for (const a of avatars.data ?? []) m[a.creator_id] = a.image;
        return m;
    }, [avatars.data]);
    const campaign = useMemo(() => {
        if (!shell.data || !roster.data) return undefined;
        return {
            ...shell.data,
            creators: roster.data.creators,
            creator_metas: roster.data.creator_metas,
            deliverables2: roster.data.deliverables2,
            creator_deliverables: roster.data.creator_deliverables,
        };
    }, [shell.data, roster.data]);
    const isLoading = shell.isLoading || roster.isLoading;
    const isError = shell.isError || roster.isError;
    const refetchCampaign = () => {
        void shell.refetch();
        void roster.refetch();
    };
    const append = useAppendCreators();
    const update = useUpdateCampaign();
    const updateMeta = useUpdateCreatorMeta();
    const fetchCreatorStory = useFetchCreatorStory();
    const user = useAtomValue(userAtom);
    const uid = user?.uid;
    const columnPref = useCreatorColumnPref(uid);
    const saveColumns = useSaveCreatorColumns();
    const appliedCols = useRef<string | undefined>();

    // Column visibility. Default stays the current set until this account saves its own.
    const [visibleCols, setVisibleCols] = useState<ColKey[]>(() => storedCols(accountColKey(uid)) ?? storedCols(COLS_STORAGE_KEY) ?? DEFAULT_VISIBLE);
    useEffect(() => {
        if (!uid || !columnPref.isSuccess || appliedCols.current === uid) return;
        appliedCols.current = uid;
        const saved = columnPref.data?.columns?.length ? knownCols(columnPref.data.columns) : null;
        if (saved?.length) {
            setVisibleCols(saved);
            localStorage.setItem(accountColKey(uid), JSON.stringify(saved));
            return;
        }
        const local = storedCols(accountColKey(uid)) ?? storedCols(COLS_STORAGE_KEY);
        if (local) {
            setVisibleCols(local);
            saveColumns.mutate({ uid, columns: local }, {
                onError: () => notification.error({ message: 'Could not save your column choice' }),
            });
        }
    }, [uid, columnPref.isSuccess, columnPref.data]);
    const toggleCol = (key: ColKey) => {
        setVisibleCols((prev) => {
            const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
            if (!next.length) return prev;
            if (uid) {
                localStorage.setItem(accountColKey(uid), JSON.stringify(next));
                saveColumns.mutate({ uid, columns: next }, {
                    onError: () => notification.error({ message: 'Could not save your column choice' }),
                });
            }
            return next;
        });
    };
    const col = (key: ColKey) => visibleCols.includes(key);

    // Per-creator local edits: keyed by creator id
const [metaMap, setMetaMap] = useState<Record<number, CreatorMetaDraft>>({}); 

    // Initialise metaMap from the campaign's creator_metas whenever data arrives
    useEffect(() => {
        if (!campaign?.creator_metas) return;
        const next: Record<number, CreatorMetaDraft> = {};
        for (const m of campaign.creator_metas) {
            next[m.creator_id] = {
                budget: m.budget ?? null,
                comments: m.comments ?? '',
                approved: m.approved ?? null,
                pillar: m.pillar ?? '',
                relationship_stage: m.relationship_stage || 'unaware',
                commission: m.commission ?? null,
                paid_amount: m.paid_amount ?? null,
                payment_status: m.payment_status || 'unpaid',
                contract_url: m.contract_url ?? '',
                contract_status: m.contract_status ?? '',
                po_number: m.po_number ?? '',
            };
        }
        setMetaMap(next);
    }, [campaign?.creator_metas]);

    // Creator search state
    const [creatorSearch, setCreatorSearch] = useState('');
    const [creatorOptions, setCreatorOptions] = useState<{ label: string; value: number }[]>([]);
    const [creatorSearchLoading, setCreatorSearchLoading] = useState(false);

    useEffect(() => {
        const handle = setTimeout(() => {
            setCreatorSearchLoading(true);
            axiosApiInstance
                .get<API.DataResponse<CampaignCreator[]>>('/api/creators/items', {
                    params: {
                        search: creatorSearch,
                        page: 1,
                        page_size: 50,
                        youtube_followers_min: 0,
                        youtube_followers_max: 1000000,
                        instagram_followers_min: 0,
                        instagram_followers_max: 1000000,
                        tiktok_followers_min: 0,
                        tiktok_followers_max: 1000000,
                    }
                })
                .then(({ data }) => {
                    setCreatorOptions(
                        (data?.data ?? []).map((c) => ({
                            label: c.creator || `Creator #${c.id}`,
                            value: c.id,
                        }))
                    );
                })
                .catch((err) => console.error('creator search failed', err))
                .finally(() => setCreatorSearchLoading(false));
        }, 300);
        return () => clearTimeout(handle);
    }, [creatorSearch]);

    const handleAddCreator = async (creatorId: number) => {
        if (!campaign?.id) return;
        try {
            await append.mutateAsync({ id: campaign.id, creatorIds: [creatorId] });
            await refetchCampaign();
            notification.success({ message: 'Creator added', placement: 'bottomRight', duration: 2 });
        } catch {
            notification.error({ message: 'Failed to add creator' });
        }
    };

    const handleRemoveCreator = async (creatorId: number) => {
        if (!campaign?.id) return;
        try {
            await update.mutateAsync({
                id: campaign.id,
                name: campaign.name,
                project: campaign.project,
                notes: campaign.notes,
                brief: campaign.brief,
                budget: campaign.budget,
                deliverables: campaign.deliverables,
                brand_id: campaign.brand_id,
                status: campaign.status,
                start_date: campaign.start_date,
                end_date: campaign.end_date,
                creators: (campaign.creators ?? []).filter((c) => c.id !== creatorId),
            } as Campaign & { id: number });
            notification.success({ message: 'Creator removed', placement: 'bottomRight', duration: 2 });
        } catch {
            notification.error({ message: 'Failed to remove creator' });
        }
    };

    const savePanel = async (creatorId: number, patch: Partial<CreatorMetaDraft>) => {
        if (!campaign?.id) return;
        const current = metaMap[creatorId] ?? EMPTY_META;
        try {
            await updateMeta.mutateAsync({
                campaignId: campaign.id,
                creatorId,
                ...patch,
            });
            setMetaMap((prev) => ({ ...prev, [creatorId]: { ...current, ...patch } }));
        } catch {
            notification.error({ message: 'Failed to save' });
        }
    };

    // Summary stats — only count creators explicitly marked approved
    const [sortCol, setSortCol] = useState<'yt' | 'ig' | 'tt' | 'budget' | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [profileCreatorId, setProfileCreatorId] = useState<number | null>(null);
    const [panelCreatorId, setPanelCreatorId] = useState<number | null>(null);
    const [fetchingCreatorId, setFetchingCreatorId] = useState<number | null>(null);
    const [tableSearch, setTableSearch] = useState('');

    const handleCreatorSelect = (creator: CampaignCreator) => setPanelCreatorId(creator.id);

    const handleFetchStory = async (creator: CampaignCreator) => {
        if (!campaign?.id) return;

        const creatorName = creator.creator || `Creator #${creator.id}`;
        setFetchingCreatorId(creator.id);

        const key = `fetch-story-${creator.id}`;
        notification.info({
            key,
            message: `Fetching Stories & AI Check`,
            description: `Searching current Instagram stories for ${creatorName} and running campaign AI verification...`,
            duration: 0,
        });

        try {
            const res = await fetchCreatorStory.mutateAsync({
                campaignId: campaign.id,
                creatorId: creator.id,
            });

            if (res.matched || res.status === 'published') {
                notification.success({
                    key,
                    message: `Story Matched Campaign!`,
                    description: res.message || `Found matching story for ${creatorName}! It was automatically moved to Published.`,
                    duration: 4,
                });
            } else {
                notification.info({
                    key,
                    message: `Story Saved to Drafts`,
                    description: res.message || `No campaign keywords matched for ${creatorName}. Saved to Drafts.`,
                    duration: 4,
                });
            }
        } catch (err: any) {
            notification.warning({
                key,
                message: `Story Fetch`,
                description: err?.response?.data?.error || `Could not fetch story for ${creatorName}.`,
                duration: 4,
            });
        } finally {
            setFetchingCreatorId(null);
        }
    };


    // Summary stats — only count creators explicitly marked approved
    const approvedStats = useMemo(() => {
        const approved = (campaign?.creators ?? []).filter(
            (c) => metaMap[c.id]?.approved === true
        );
        let ytFollowers = 0, igFollowers = 0, ttFollowers = 0, totalBudget = 0;
        for (const c of approved) {
            ytFollowers += c.accounts?.find((a) => a.platform?.name === 'YouTube'   || a.platform_id === 1)?.following ?? 0;
            igFollowers += c.accounts?.find((a) => a.platform?.name === 'Instagram' || a.platform_id === 2)?.following ?? 0;
            ttFollowers += c.accounts?.find((a) => a.platform?.name === 'TikTok'    || a.platform_id === 3)?.following ?? 0;
            totalBudget += metaMap[c.id]?.budget ?? 0;
        }
        return { ytFollowers, igFollowers, ttFollowers, totalBudget, count: approved.length };
    }, [campaign?.creators, metaMap]);

    if (isLoading) {
        return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;
    }

    if (isError || !campaign) {
        return <Empty description="Campaign not found" />;
    }

    const linkedIds = new Set((campaign.creators ?? []).map((c) => c.id));
    const filteredOptions = creatorOptions.filter((o) => !linkedIds.has(o.value));

    type SortCol = 'yt' | 'ig' | 'tt' | 'budget';
    const handleSort = (col: SortCol) => {
        if (sortCol === col) {
            setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortCol(col);
            setSortDir('desc');
        }
    };

    const SortIcon: React.FC<{ col: SortCol }> = ({ col: c }) => (
        <span style={{ marginLeft: 4, opacity: sortCol === c ? 1 : 0.3, fontSize: 10 }}>
            {sortCol === c && sortDir === 'asc' ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </span>
    );

    const rawCreators = campaign.creators ?? [];
    const creators = sortCol
        ? [...rawCreators].sort((a, b) => {
            let va = 0, vb = 0;
            if (sortCol === 'yt') {
                va = a.accounts?.find((acc) => acc.platform?.name === 'YouTube' || acc.platform_id === 1)?.following ?? 0;
                vb = b.accounts?.find((acc) => acc.platform?.name === 'YouTube' || acc.platform_id === 1)?.following ?? 0;
            } else if (sortCol === 'ig') {
                va = a.accounts?.find((acc) => acc.platform?.name === 'Instagram' || acc.platform_id === 2)?.following ?? 0;
                vb = b.accounts?.find((acc) => acc.platform?.name === 'Instagram' || acc.platform_id === 2)?.following ?? 0;
            } else if (sortCol === 'tt') {
                va = a.accounts?.find((acc) => acc.platform?.name === 'TikTok' || acc.platform_id === 3)?.following ?? 0;
                vb = b.accounts?.find((acc) => acc.platform?.name === 'TikTok' || acc.platform_id === 3)?.following ?? 0;
            } else if (sortCol === 'budget') {
                va = metaMap[a.id]?.budget ?? 0;
                vb = metaMap[b.id]?.budget ?? 0;
            }
            return sortDir === 'asc' ? va - vb : vb - va;
          })
        : rawCreators;
    const visibleCreators = tableSearch.trim()
        ? creators.filter((c) => (c.creator || '').toLowerCase().includes(tableSearch.trim().toLowerCase()))
        : creators;

    return (
        <>
        <div>
            <Collapse ghost defaultActiveKey={['details']} style={{ background: '#fff' }}>
                <Collapse.Panel key="details" header={<span style={{ fontSize: 15 }}>Campaign details</span>}>
            <Descriptions column={2} bordered size="middle" labelStyle={{ background: '#fff', fontSize: 14 }} contentStyle={{ background: '#fff', fontSize: 15 }}>
                <Descriptions.Item label="Name">{campaign.name}</Descriptions.Item>
                <Descriptions.Item label="Status">
                    <Tag color={statusColor(campaign.status)}>{campaign.status || 'spec'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Brand">
                    {campaign.brand?.name ? (
                        <Tag color="geekblue" style={{ margin: 0 }}>{campaign.brand.name}</Tag>
                    ) : (
                        <span style={{ color: '#bbb' }}>—</span>
                    )}
                </Descriptions.Item>
                <Descriptions.Item label="Budget (€)">
                    {campaign.budget != null
                        ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(campaign.budget)
                        : <span style={{ color: '#bbb' }}>—</span>}
                </Descriptions.Item>
                <Descriptions.Item label="Start Date">
                    {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString() : <span style={{ color: '#bbb' }}>—</span>}
                </Descriptions.Item>
                <Descriptions.Item label="End Date">
                    {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString() : <span style={{ color: '#bbb' }}>—</span>}
                </Descriptions.Item>

                {/* Paid Media */}
                <Descriptions.Item label="Paid Media">
                    {campaign.paid_media
                        ? <Tag color="green">Yes</Tag>
                        : <Tag color="default">No</Tag>}
                </Descriptions.Item>
                {(campaign.paid_media_start || campaign.paid_media_end) && (
                    <Descriptions.Item label="Paid Media Period">
                        {campaign.paid_media_start ? new Date(campaign.paid_media_start).toLocaleDateString() : '?'} – {campaign.paid_media_end ? new Date(campaign.paid_media_end).toLocaleDateString() : '?'}
                    </Descriptions.Item>
                )}

                {/* Competition */}
                {(campaign.competition_start || campaign.competition_end) && (
                    <Descriptions.Item label="Competition Period">
                        {campaign.competition_start ? new Date(campaign.competition_start).toLocaleDateString() : '?'} – {campaign.competition_end ? new Date(campaign.competition_end).toLocaleDateString() : '?'}
                    </Descriptions.Item>
                )}
                {campaign.competition_comments && (
                    <Descriptions.Item label="Competition Comments">
                        <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>{campaign.competition_comments}</Typography.Text>
                    </Descriptions.Item>
                )}

                {/* Deliverable rules */}
                <Descriptions.Item label="Deliverable Rules" span={2}>
                    {(campaign.deliverables2 ?? []).length > 0
                        ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {(campaign.deliverables2 ?? []).map((d, i) => (
                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    {PLATFORM_ICON[d.platform]}
                                    <span style={{ fontSize: 12 }}>{d.post_type} ×{d.quantity}</span>
                                </span>
                            ))}
                          </div>
                        : <span style={{ color: '#bbb' }}>—</span>}
                </Descriptions.Item>

                {campaign.brief && (
                    <Descriptions.Item label="Brief" span={2}>
                        <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                            {campaign.brief}
                        </Typography.Paragraph>
                    </Descriptions.Item>
                )}
                {campaign.deliverables && (
                    <Descriptions.Item label="Deliverables" span={2}>
                        <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                            {campaign.deliverables}
                        </Typography.Paragraph>
                    </Descriptions.Item>
                )}
                <Descriptions.Item label="Updated">
                    {campaign.updated_at ? new Date(campaign.updated_at).toLocaleString() : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                    {campaign.created_at ? new Date(campaign.created_at).toLocaleString() : '-'}
                </Descriptions.Item>
            </Descriptions>
                </Collapse.Panel>
            </Collapse>

            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, margin: '16px 0 8px', border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                <thead>
                    <tr>
                        {['Approved', 'YouTube', 'Instagram', 'TikTok', 'Creators budget', 'Left of budget'].map((label) => (
                            <th key={label} style={{ ...thStyle, textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>{label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {[
                            String(approvedStats.count),
                            formatFollowers(approvedStats.ytFollowers),
                            formatFollowers(approvedStats.igFollowers),
                            formatFollowers(approvedStats.ttFollowers),
                            `€${approvedStats.totalBudget.toLocaleString()}`,
                            campaign.budget != null ? `€${(campaign.budget - approvedStats.totalBudget).toLocaleString()}` : '—',
                        ].map((value, i) => (
                            <td key={i} style={{ ...tdStyle, textAlign: 'center', fontSize: 22, fontWeight: 600 }}>{value}</td>
                        ))}
                    </tr>
                </tbody>
            </table>

            <div style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <Typography.Title level={4} style={{ margin: 0, fontSize: 20 }}>
                        Creators ({creators.length})
                    </Typography.Title>
                    <Popover
                        trigger="click"
                        placement="bottomRight"
                        content={
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 200, maxHeight: 360, overflow: 'auto' }}>
                                {CAMPAIGN_CREATOR_COLS.filter((c) => !EXTRA_COLS.has(c.key)).map(({ key, label }) => (
                                    <Checkbox key={key} checked={col(key)} onChange={() => toggleCol(key)}>{label}</Checkbox>
                                ))}
                                <Typography.Text type="secondary" style={{ marginTop: 4 }}>More</Typography.Text>
                                {CAMPAIGN_CREATOR_COLS.filter((c) => EXTRA_COLS.has(c.key)).map(({ key, label }) => (
                                    <Checkbox key={key} checked={col(key)} onChange={() => toggleCol(key)}>{label}</Checkbox>
                                ))}
                            </div>
                        }
                    >
                        <Button size="small" icon={<SettingOutlined />}>Columns</Button>
                    </Popover>
                </div>

                <Select
                    showSearch
                    allowClear
                    value={null}
                    placeholder="Search and add a creator…"
                    filterOption={false}
                    loading={creatorSearchLoading || append.isLoading}
                    disabled={append.isLoading}
                    onSearch={setCreatorSearch}
                    options={filteredOptions}
                    onChange={(creatorId: number) => handleAddCreator(creatorId)}
                    notFoundContent={creatorSearchLoading ? 'Searching…' : 'No creators found'}
                    style={{ width: 360, marginBottom: 16, marginRight: 12 }}
                />
                <Input.Search
                    allowClear
                    placeholder="Search for creator"
                    style={{ width: 240, marginBottom: 16 }}
                    onChange={(e) => setTableSearch(e.target.value)}
                />

                {visibleCreators.length === 0 ? (
                    <Empty description="No creators linked yet" />
                ) : (
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <th style={thStyle}></th>
                                <th style={thStyle}>Creator</th>
                                {col('country') && <th style={thStyle}>Country</th>}
                                {col('stage') && <th style={thStyle}>Relationship stage</th>}
                                {col('tier') && <th style={thStyle}>Tier</th>}
                                {col('tags') && <th style={thStyle}>Tags</th>}
                                {col('audience') && <th style={{ ...thStyle, textAlign: 'right' }}>Total audience</th>}
                                {col('ig_audience') && <th style={{ ...thStyle, textAlign: 'right' }}>Instagram audience</th>}
                                {col('approved') && <th style={{ ...thStyle, textAlign: 'center' }}>Approved</th>}
                                {col('pillar') && <th style={thStyle}>Pillar</th>}
                                {col('yt') && <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('yt')}>{YT_ICON}<SortIcon col="yt" /></th>}
                                {col('ig') && <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('ig')}>{IG_ICON}<SortIcon col="ig" /></th>}
                                {col('tt') && <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('tt')}>{TT_ICON}<SortIcon col="tt" /></th>}
                                {col('budget') && <th style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('budget')}>Total Budget (€)<SortIcon col="budget" /></th>}
                                {col('comments') && <th style={thStyle}>Comments</th>}
                                {col('deliverables') && <th style={thStyle}>Deliverables</th>}
                                {col('city') && <th style={thStyle}>City</th>}
                                {col('email') && <th style={thStyle}>Email</th>}
                                {col('phone') && <th style={thStyle}>Phone</th>}
                                {col('main_platform') && <th style={thStyle}>Main platform</th>}
                                {col('handles') && <th style={thStyle}>Handles</th>}
                                {col('engagement') && <th style={{ ...thStyle, textAlign: 'right' }}>Engagement</th>}
                                {col('commission') && <th style={{ ...thStyle, textAlign: 'right' }}>Commission</th>}
                                {col('paid') && <th style={{ ...thStyle, textAlign: 'right' }}>Paid</th>}
                                {col('payment_status') && <th style={thStyle}>Payment</th>}
                                {col('contract_status') && <th style={thStyle}>Contract</th>}
                                <th style={thStyle}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleCreators.map((c: CampaignCreator) => {
                                const meta = metaMap[c.id] ?? EMPTY_META;

                                const cycleApproved = (current: boolean | null): boolean | null => {
                                    if (current == null) return true;
                                    if (current === true) return false;
                                    return null;
                                };

                                const avatarSrc = avatarByCreator[c.id];

                                const ytAccount = c.accounts?.find(
                                    (a) => a.platform?.name === 'YouTube' || a.platform_id === 1,
                                );
                                const igAccount = c.accounts?.find(
                                    (a) => a.platform?.name === 'Instagram' || a.platform_id === 2,
                                );
                                const ttAccount = c.accounts?.find(
                                    (a) => a.platform?.name === 'TikTok' || a.platform_id === 3,
                                );
                                const ytFollowers = ytAccount?.following;
                                const igFollowers = igAccount?.following;
                                const ttFollowers = ttAccount?.following;

                                const saveMeta = async (budget: number | null, comments: string, approved: boolean | null, pillar: string, relationship_stage?: string) => {
                                    if (!campaign?.id) return;
                                    try {
                                        await updateMeta.mutateAsync({
                                            campaignId: campaign.id,
                                            creatorId: c.id,
                                            budget,
                                            comments,
                                            approved,
                                            pillar,
                                            relationship_stage: relationship_stage ?? meta.relationship_stage,
                                        });
                                    } catch {
                                        notification.error({ message: 'Failed to save creator meta' });
                                    }
                                };

                                return (
                                    <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        {/* avatar */}
                                        <td style={tdStyle}>
                                            <Avatar
                                                src={avatarSrc ? `data:image/jpeg;base64,${avatarSrc}` : undefined}
                                                size={40}
                                                style={{ cursor: 'pointer', background: '#d3adf7' }}
                                                onClick={() => handleCreatorSelect(c)}
                                            >
                                                {!avatarSrc && (c.creator?.[0] ?? '?').toUpperCase()}
                                            </Avatar>
                                        </td>
                                        {/* name */}
                                        <td style={tdStyle}>
                                            <Typography.Text
                                                strong
                                                style={{ cursor: 'pointer', fontSize: 16 }}
                                                onClick={() => handleCreatorSelect(c)}
                                            >
                                                {c.creator || `Creator #${c.id}`}
                                            </Typography.Text>
                                                                                        <Button
                                                size="small"
                                                icon={<InstagramOutlined />}
                                                loading={fetchingCreatorId === c.id}
                                                disabled={fetchingCreatorId !== null && fetchingCreatorId !== c.id}
                                                onClick={() => handleFetchStory(c)}
                                                style={{ marginLeft: 10 }}
                                            >
                                                Fetch story
                                            </Button>
                                        </td>
                                        {col('country') && <td style={tdStyle}>{creatorCountry(c) || <span style={{ color: '#d9d9d9' }}>--</span>}</td>}
                                        {col('stage') && <td style={tdStyle}>
                                            <Select
                                                size="small"
                                                style={{ width: 130 }}
                                                value={meta.relationship_stage || 'unaware'}
                                                options={STAGE_OPTIONS}
                                                onChange={(v) => {
                                                    const updated = { ...meta, relationship_stage: v };
                                                    setMetaMap((prev) => ({ ...prev, [c.id]: updated }));
                                                    saveMeta(updated.budget, updated.comments, updated.approved, updated.pillar, v);
                                                }}
                                            />
                                        </td>}
                                        {col('tier') && <td style={tdStyle}>
                                            <Tag>{audienceTier(totalAudience(c))}</Tag>
                                        </td>}
                                        {col('tags') && <td style={tdStyle}><CreatorTags creator={c} /></td>}
                                        {col('audience') && <td style={{ ...tdStyle, textAlign: 'right' }}>{formatFollowers(totalAudience(c))}</td>}
                                        {col('ig_audience') && <td style={{ ...tdStyle, textAlign: 'right' }}>
                                            {igFollowers != null
                                                ? formatFollowers(igFollowers)
                                                : <span style={{ color: '#d9d9d9' }}>--</span>}
                                        </td>}
                                        {/* approved */}
                                        {col('approved') && <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <span
                                                onClick={() => {
                                                    const next = cycleApproved(meta.approved);
                                                    const updated = { ...meta, approved: next };
                                                    setMetaMap((prev) => ({ ...prev, [c.id]: updated }));
                                                    saveMeta(updated.budget, updated.comments, updated.approved, updated.pillar);
                                                }}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 24,
                                                    height: 24,
                                                    border: `1px solid ${
                                                        meta.approved === true ? '#52c41a'
                                                        : meta.approved === false ? '#ff4d4f'
                                                        : '#d9d9d9'
                                                    }`,
                                                    borderRadius: 4,
                                                    cursor: 'pointer',
                                                    background: meta.approved === true ? '#f6ffed' : meta.approved === false ? '#fff2f0' : '#fff',
                                                    color: meta.approved === true ? '#52c41a' : '#ff4d4f',
                                                    fontSize: 12,
                                                    userSelect: 'none',
                                                }}
                                            >
                                                {meta.approved === true && <CheckOutlined />}
                                                {meta.approved === false && <CloseOutlined />}
                                            </span>
                                        </td>}
                                        {/* pillar */}
                                        {col('pillar') && <td style={tdStyle}>
                                            <Input
                                                size="small"
                                                style={{ width: 140, fontSize: 15 }}
                                                value={meta.pillar}
                                                onChange={(e) =>
                                                    setMetaMap((prev) => ({
                                                        ...prev,
                                                        [c.id]: { ...prev[c.id] ?? EMPTY_META, pillar: e.target.value },
                                                    }))
                                                }
                                                onBlur={() => saveMeta(meta.budget, meta.comments, meta.approved, meta.pillar)}
                                            />
                                        </td>}
                                        {/* YT followers */}
                                        {col('yt') && <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            {ytFollowers != null
                                                ? <a href={ytAccount?.key} target="_blank" rel="noreferrer" style={{ color: '#666' }}>{formatFollowers(ytFollowers)}</a>
                                                : <span style={{ color: '#d9d9d9' }}>—</span>}
                                        </td>}
                                        {/* IG followers */}
                                        {col('ig') && <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            {igFollowers != null
                                                ? <a href={igAccount?.key} target="_blank" rel="noreferrer" style={{ color: '#666' }}>{formatFollowers(igFollowers)}</a>
                                                : <span style={{ color: '#d9d9d9' }}>—</span>}
                                        </td>}
                                        {/* TT followers */}
                                        {col('tt') && <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            {ttFollowers != null
                                                ? <a href={ttAccount?.key} target="_blank" rel="noreferrer" style={{ color: '#666' }}>{formatFollowers(ttFollowers)}</a>
                                                : <span style={{ color: '#d9d9d9' }}>—</span>}
                                        </td>}
                                        {/* budget */}
                                        {col('budget') && <td style={tdStyle}>
                                            <InputNumber
                                                size="small"
                                                style={{ width: 120 }}
                                                prefix="€"
                                                min={0}
                                                step={100}
                                                precision={0}
                                                value={meta.budget ?? undefined}
                                                onChange={(val) =>
                                                    setMetaMap((prev) => ({
                                                        ...prev,
                                                        [c.id]: { ...prev[c.id] ?? EMPTY_META, budget: val ?? null },
                                                    }))
                                                }
                                                onBlur={() => saveMeta(meta.budget, meta.comments, meta.approved, meta.pillar)}
                                            />
                                        </td>}
                                        {/* comments */}
                                        {col('comments') && <td style={tdStyle}>
                                            <Input.TextArea
                                                size="small"
                                                autoSize={{ minRows: 1, maxRows: 3 }}
                                                style={{ width: 220, fontSize: 15, resize: 'none' }}
                                                value={meta.comments}
                                                onChange={(e) =>
                                                    setMetaMap((prev) => ({
                                                        ...prev,
                                                        [c.id]: { ...prev[c.id] ?? EMPTY_META, comments: e.target.value },
                                                    }))
                                                }
                                                onBlur={() => saveMeta(meta.budget, meta.comments, meta.approved, meta.pillar)}
                                            />
                                        </td>}
                                        {/* deliverables */}
                                        {col('deliverables') && <td style={tdStyle}>
                                            {campaign?.id && (
                                                <CreatorDeliverables
                                                    campaignId={campaign.id}
                                                    creatorId={c.id}
                                                    deliverables={(campaign.creator_deliverables ?? []).filter(
                                                        (d) => d.creator_id === c.id,
                                                    )}
                                                    onSaved={refetchCampaign}
                                                />
                                            )}
                                        </td>}
                                        {col('city') && <td style={tdStyle}>{c.contact_info?.city || <Muted />}</td>}
                                        {col('email') && <td style={tdStyle}>{c.contact_info?.email || <Muted />}</td>}
                                        {col('phone') && <td style={tdStyle}>{c.contact_info?.phone_number || <Muted />}</td>}
                                        {col('main_platform') && <td style={tdStyle}>{c.main_platform?.name || <Muted />}</td>}
                                        {col('handles') && <td style={tdStyle}>{
                                            [
                                                accountHandle(ytAccount?.key) && `YT ${accountHandle(ytAccount?.key)}`,
                                                accountHandle(igAccount?.key) && `IG ${accountHandle(igAccount?.key)}`,
                                                accountHandle(ttAccount?.key) && `TT ${accountHandle(ttAccount?.key)}`,
                                            ].filter(Boolean).join(' · ') || <Muted />
                                        }</td>}
                                        {col('engagement') && <td style={{ ...tdStyle, textAlign: 'right' }}>{
                                            bestEngagement(c) != null ? `${bestEngagement(c)!.toFixed(2)}%` : <Muted />
                                        }</td>}
                                        {col('commission') && <td style={{ ...tdStyle, textAlign: 'right' }}>{money(meta.commission)}</td>}
                                        {col('paid') && <td style={{ ...tdStyle, textAlign: 'right' }}>{money(meta.paid_amount)}</td>}
                                        {col('payment_status') && <td style={tdStyle}>{meta.payment_status || 'unpaid'}</td>}
                                        {col('contract_status') && <td style={tdStyle}>{meta.contract_status || <Muted />}</td>}
                                        {/* remove */}
                                        <td style={tdStyle}>
                                            <Button
                                                danger
                                                type="text"
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                onClick={() => handleRemoveCreator(c.id)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr style={{ borderTop: '1px solid #f0f0f0' }}>
                                <td style={tdStyle} colSpan={2}>
                                    <Typography.Text type="secondary" style={{ fontSize: 15 }}>{visibleCreators.length} creator{visibleCreators.length === 1 ? '' : 's'}</Typography.Text>
                                </td>
                                <td style={tdStyle} colSpan={20} />
                            </tr>
                        </tfoot>
                    </table>
                    </div>
                )}
            </div>
        </div>
        <CreatorDrawer
            creatorId={profileCreatorId}
            onClose={() => setProfileCreatorId(null)}
        />
        {panelCreatorId != null && campaign.id && (
            <CreatorPanel
                campaignId={campaign.id}
                creators={visibleCreators}
                creatorId={panelCreatorId}
                meta={metaMap[panelCreatorId] ?? EMPTY_META}
                saving={updateMeta.isLoading}
                avatar={avatarByCreator[panelCreatorId]}
                deliverables={(
                    <CreatorDeliverables
                        campaignId={campaign.id}
                        creatorId={panelCreatorId}
                        deliverables={(campaign.creator_deliverables ?? []).filter((d) => d.creator_id === panelCreatorId)}
                        embedded
                        onSaved={refetchCampaign}
                    />
                )}
                onClose={() => setPanelCreatorId(null)}
                onChangeCreator={setPanelCreatorId}
                onViewProfile={() => setProfileCreatorId(panelCreatorId)}
                onSave={(patch) => savePanel(panelCreatorId, patch)}
            />
        )}
        </>
    );
};

export default CreatorsSection;
